import React, { useEffect, useContext, useState } from "react";
import {
  MantineProvider,
  Box,
  Container,
  Paper,
  Image,
  Center,
  Stack,
  Text,
  Group,
  Divider,
  Anchor,
  ActionIcon,
  LoadingOverlay,
  useMantineTheme,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";

import { Notifications } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import VXLogo from "./assets/vx-logo.svg";
import { DataContext } from "./context/DataContext";
import Navigation from "./components/navigation";
import FontLoaderSimple from "./components/FontLoaderSimple";
import { isMobile } from "react-device-detect";
const isIframe = window !== window.top;
import "./App.css";

const FALLBACK_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function familyStack(family) {
  if (!family) return FALLBACK_STACK;
  const quoted = family.includes(" ") ? `"${family}"` : family;
  return `${quoted}, ${FALLBACK_STACK}`;
}

// Inner component that can use the theme
function AppContent({ routes }) {
  const { text, data, settings, dispatch, postMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const isWideViewport = useMediaQuery("(min-width: 600px)");

  // Show a separate close bar at the top-right for wide iframe viewports
  const showWideCloseBar = isIframe && !isMobile && isWideViewport && settings?.isCloseButtonShown !== false;

  const handleClose = () => {
    if (dispatch) {
      dispatch({ type: "SET_CLOSED_CLICKED", payload: true });
    }
    postMessage && postMessage("CLOSE");
  };

  // Update the viewport height on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Theme-aware styles
  const getDesktopContainerStyles = () => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
  });

  const getContentStylesDirectDesktop = () => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "400px",
    border: `.5px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
    padding: "25px 25px",
    paddingTop: "10px",
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
  });

  return (
    <>
      <Notifications position="top-center" zIndex={2077} />
      {showWideCloseBar && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "8px 12px",
            borderBottom: "1px solid #ccc",
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            zIndex: 1000,
          }}
        >
          <ActionIcon variant="light" onClick={handleClose}>
            <IconX stroke={1.5} size={18} />
          </ActionIcon>
        </div>
      )}
      <div
        className={!isMobile && isWideViewport ? "desktopContainer" : ""}
        style={{
          ...(!isMobile && isWideViewport ? getDesktopContainerStyles() : {}),
          ...(isMobile || !isWideViewport ? {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white
          } : {})
        }}
      >
        <div
          style={{
            ...(isMobile ? {
              height: `calc(${viewportHeight}px - 50px)`,
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white
            } : {}),
            ...(!isMobile && isWideViewport ? getContentStylesDirectDesktop() : {})
          }}
          className={
            !isMobile && isWideViewport ? "contentStylesDirectDesktop" : "content"
          }
        >
          {settings?.isHeaderShown !== false && (
            <Navigation logo_url={data?.logo_url ? data.logo_url : ""} />
          )}
          <div style={{ paddingTop: 20, flex: 1 }}>
            {routes}
          </div>
        </div>
      </div>

      {settings?.isFooterShown !== false && (
        <Box
          align="center"
          style={{
            position: "absolute",
            bottom: 15,
            width: "100%",
            align: "center",
          }}
        >
          <Center>
            <Group spacing={10}>
              <Group spacing={5}>
                <Image src={VXLogo} width={20} />
                <Text fz="xs" c="dimmed">
                  {text.general.poweredBy} Veratad
                </Text>
              </Group>
              <Divider orientation="vertical" />
              <Anchor
                href={data?.settings?.journey?.settings?.privacyPolicy || "https://veratad.com/privacy-policy/"}
                size="xs"
                target="_blank"
              >
                {text.general.privacyPolicy}
              </Anchor>
            </Group>
          </Center>
        </Box>
      )}
    </>
  );
}

function App({ routes }) {
  const { data } = useContext(DataContext);

  if (!data) {
    return <LoadingOverlay visible />;
  }
  const buttonTextColor = data.styles?.components?.button?.color || null;
  
  // Font configuration
  const fontDef = data.styles?.global?.typography?.font || null;
  const fontStack = familyStack(fontDef?.family);

  const colors_array = data.styles?.colors
    ? data.styles.colors
    : {
        "vx-green": [
          "#eff6fb",
          "#dee9f1",
          "#b8d2e5",
          "#8fbad8",
          "#6ea5ce",
          "#5998c8",
          "#4d92c6",
          "#3e7eaf",
          "#34709d",
          "#22618b",
        ],
      };

  // Debug theme configuration
  const themeConfig = {
    //fontFamily: "Roboto, sans-serif",
    colors: colors_array,
    colorScheme: data.styles?.colorScheme
      ? data.styles.colorScheme
      : "light",
  };

  return (
    <MantineProvider
      theme={{
        ...themeConfig,
        primaryColor: data.styles?.primaryColor
          ? data.styles.primaryColor
          : "vx-green",
        primaryShade: data.styles?.primaryShade ? data.styles.primaryShade : 5,
        // Use custom family globally when provided (text + headings)
        fontFamily: fontDef ? fontStack : undefined,
        headings: fontDef ? { fontFamily: fontStack } : undefined,
        components: {
          Button: {
            defaultProps: {
              radius: data.styles?.button?.radius
                ? data.styles.button.radius
                : 6,
            },
            styles: (theme, params, { variant }) => {
              // In Mantine v5, variant is in the third context arg, not params
              const isFilledVariant = variant === "filled" || !variant;

              return {
                root: {
                  // Only apply buttonTextColor to filled (primary) buttons
                  ...(isFilledVariant && buttonTextColor
                    ? {
                        color: buttonTextColor,
                        // keep icons in sync
                        "& svg": { color: buttonTextColor, fill: "currentColor" },
                      }
                    : {}),
                },
              };
            },
          },
        },
        defaultRadius: data.styles?.defaultRadius ? data.defaultRadius : 6,
        breakpoints: {
          xs: 340, // Extra small screen breakpoint in pixels
          sm: 640, // Small screen breakpoint in pixels
          md: 960, // Medium screen breakpoint in pixels
          lg: 1280, // Large screen breakpoint in pixels
          xl: 1440, // Extra large screen breakpoint in pixels
        },
      }}
      withGlobalStyles
      withNormalizeCSS
    >
      {fontDef && <FontLoaderSimple styles={data.styles} />}
      <AppContent routes={routes} />
    </MantineProvider>
  );
}

export default App;
